import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  Alert, Box, Button, Chip, LinearProgress, Paper, Stack,
  Table, TableBody, TableCell, TableHead, TableRow, TextField, Tooltip, Typography,
} from '@mui/material';
import { api } from '../api/client';
import { AddressWithCopy, PageHeader } from '../components/common';

interface ClaimVerification {
  exists: boolean;
  issuer?: string;
  issuerIsOwner?: boolean;
  validTo?: number;
  validToIso?: string;
  revoked?: boolean;
  expired?: boolean;
  signer?: string | null;
  signatureValid?: boolean;
}

interface RegistryAccount {
  wallet: string;
  registered: boolean;
  uid: string | null;
  countryCode: number | null;
  isVerified: boolean;
  claim: ClaimVerification;
  source: 'onchain' | 'db' | 'both';
  account: { loginId: string; keyType: string; keyStatus: string } | null;
}

interface RegistryList {
  registry: string;
  owner: string;
  claimSigner: string | null;
  signerIsIssuer: boolean;
  total: number;
  truncated: boolean;
  accounts: RegistryAccount[];
}

const SOURCE_LABEL: Record<RegistryAccount['source'], string> = {
  both: '온체인 + DB',
  onchain: '온체인만',
  db: 'DB만',
};

function ClaimCell({ c }: { c: ClaimVerification }) {
  if (!c.exists) return <Chip size="small" label="클레임 없음" color="default" variant="outlined" />;
  if (c.revoked) return <Chip size="small" label="회수됨" color="warning" variant="outlined" />;
  if (c.expired) return <Chip size="small" label="만료" color="warning" variant="outlined" />;
  return (
    <Tooltip title={`유효기한 ${c.validToIso?.slice(0, 10) ?? '-'}`}>
      <Chip size="small" label="유효" color="success" variant="outlined" />
    </Tooltip>
  );
}

function SignatureCell({ c }: { c: ClaimVerification }) {
  if (!c.exists) return <span style={{ color: '#98a2b3' }}>—</span>;
  if (c.signatureValid) {
    return (
      <Tooltip title={`복원된 서명자 ${c.signer} = 발행자(issuer)`}>
        <Chip size="small" label="✓ 서명 검증됨" color="success" />
      </Tooltip>
    );
  }
  const reason = !c.signer
    ? '서명 형식이 아니어서 복원 불가 (구버전 자리표시자)'
    : `복원된 서명자 ${c.signer} 가 발행자 ${c.issuer} 와 다름`;
  return (
    <Tooltip title={reason}>
      <Chip size="small" label="검증 실패" color="error" variant="outlined" />
    </Tooltip>
  );
}

/**
 * 아이덴티티 레지스트리 등록 계정 현황 (SFR-006 체크리스트 94번).
 *
 * 컨트랙트에 열거 함수가 없어 서버가 온체인 이벤트와 DB 지갑을 합쳐 후보를 만든 뒤
 * **계정마다 온체인으로 되물어** 확정한다. 그래서 이 표 자체가 "화면·온체인 대조"다.
 * 클레임 서명 검증(47번, ecrecover)도 같은 표에서 보여준다.
 */
export function AdminRegistry() {
  const [q, setQ] = useState('');
  const [applied, setApplied] = useState('');

  const list = useQuery({
    queryKey: ['admin-registry', applied],
    queryFn: () => api.get<RegistryList>(`/admin/registry/accounts${applied ? `?q=${encodeURIComponent(applied)}` : ''}`),
  });

  const d = list.data;
  const accounts = d?.accounts ?? [];
  const verified = accounts.filter((a) => a.isVerified).length;
  const sigOk = accounts.filter((a) => a.claim.signatureValid).length;
  const sigBad = accounts.filter((a) => a.claim.exists && !a.claim.signatureValid).length;

  return (
    <Box>
      <PageHeader
        crumb="시스템 관리자 · 레지스트리"
        title="레지스트리 계정"
        desc="아이덴티티 레지스트리에 등록된 계정을 온체인에서 직접 읽어 보여줍니다. 클레임 서명은 관리자 공개키 복원(ecrecover)으로 검증합니다."
      />

      {list.isLoading && <LinearProgress sx={{ mb: 2 }} />}
      {list.isError && <Alert severity="error" sx={{ mb: 2 }}>레지스트리 조회에 실패했습니다.</Alert>}

      {d && (
        <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
          <Stack direction="row" spacing={4} flexWrap="wrap" useFlexGap>
            <Box>
              <Typography variant="caption" color="text.secondary">레지스트리</Typography>
              <Box><AddressWithCopy address={d.registry} /></Box>
            </Box>
            <Box>
              <Typography variant="caption" color="text.secondary">발행자(owner)</Typography>
              <Box><AddressWithCopy address={d.owner} /></Box>
            </Box>
            <Box>
              <Typography variant="caption" color="text.secondary">등록 계정</Typography>
              <Typography variant="h6">{d.total}</Typography>
            </Box>
            <Box>
              <Typography variant="caption" color="text.secondary">적격(isVerified)</Typography>
              <Typography variant="h6">{verified}</Typography>
            </Box>
            <Box>
              <Typography variant="caption" color="text.secondary">서명 검증</Typography>
              <Typography variant="h6">
                {sigOk}
                {sigBad > 0 && <span style={{ color: '#d32f2f', fontSize: 14 }}> / 실패 {sigBad}</span>}
              </Typography>
            </Box>
          </Stack>

          {!d.signerIsIssuer && (
            <Alert severity="warning" sx={{ mt: 2 }}>
              클레임 서명 키({d.claimSigner ?? '미설정'})가 발행자(owner {d.owner})와 다릅니다.
              컨트랙트가 발행자를 owner 로 고정하므로 <b>이 상태에서 새로 발급되는 클레임도 서명 검증에 실패합니다.</b>
              레지스트리 소유권을 서명 키로 이전하거나, CLAIM_SIGNER_KEY_ID·CLAIM_SIGNER_ADDRESS 를 owner 키로 지정해야 합니다.
            </Alert>
          )}
          {sigBad > 0 && d.signerIsIssuer && (
            <Alert severity="info" sx={{ mt: 2 }}>
              서명 검증에 실패한 {sigBad}건은 클레임 서명이 KMS 서명으로 바뀌기 전(v0.5.4 이하)에 발급된 계정입니다.
              키 관리 화면에서 키를 교체하면 새 서명으로 재발급됩니다.
            </Alert>
          )}
        </Paper>
      )}

      <Stack direction="row" spacing={1} sx={{ mb: 2 }}>
        <TextField
          size="small" label="지갑 주소 검색" value={q}
          onChange={(e) => setQ(e.target.value)}
          onKeyDown={(e) => { if (e.key === 'Enter') setApplied(q); }}
          sx={{ width: 360 }}
        />
        <Button variant="outlined" onClick={() => setApplied(q)}>조회</Button>
        <Button onClick={() => { setQ(''); setApplied(''); }}>초기화</Button>
      </Stack>

      <Paper variant="outlined">
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>지갑</TableCell>
              <TableCell>계정</TableCell>
              <TableCell>국가</TableCell>
              <TableCell>등록</TableCell>
              <TableCell>적격</TableCell>
              <TableCell>KYC 클레임</TableCell>
              <TableCell>서명 검증</TableCell>
              <TableCell>출처</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {accounts.length === 0 && !list.isLoading && (
              <TableRow><TableCell colSpan={8}>등록된 계정이 없습니다.</TableCell></TableRow>
            )}
            {accounts.map((a) => (
              <TableRow key={a.wallet} hover>
                <TableCell><AddressWithCopy address={a.wallet} /></TableCell>
                <TableCell>
                  {a.account ? (
                    <>
                      {a.account.loginId}
                      <Typography variant="caption" color="text.secondary" display="block">
                        {a.account.keyType} · {a.account.keyStatus}
                      </Typography>
                    </>
                  ) : (
                    <span style={{ color: '#98a2b3' }}>서비스 계정 아님</span>
                  )}
                </TableCell>
                <TableCell>{a.countryCode ?? '—'}</TableCell>
                <TableCell>
                  {a.registered
                    ? <Chip size="small" label="등록" color="primary" variant="outlined" />
                    : <Chip size="small" label="미등록" variant="outlined" />}
                </TableCell>
                <TableCell>
                  {a.isVerified
                    ? <Chip size="small" label="✓" color="success" />
                    : <Chip size="small" label="—" variant="outlined" />}
                </TableCell>
                <TableCell><ClaimCell c={a.claim} /></TableCell>
                <TableCell><SignatureCell c={a.claim} /></TableCell>
                <TableCell>
                  <Typography variant="caption" color="text.secondary">{SOURCE_LABEL[a.source]}</Typography>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Paper>

      {d?.truncated && (
        <Alert severity="info" sx={{ mt: 2 }}>
          계정이 많아 앞의 {accounts.length}건만 표시했습니다. 지갑 주소로 검색해 좁혀 보세요.
        </Alert>
      )}
    </Box>
  );
}
